import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getPortalJobs, getClientOverview,
  type PortalJob, type ClientJobProgress,
} from '@lib/api/portal';
import { LargeTitleHeader, PullToRefresh } from '@/components/common';

// ── CR #156: the Projects tab ──────────────────────────────────────────────
// One card per project, in plain words: what it's called, where it stands,
// when it runs, and how far along the shared steps are. Tapping a card opens
// the full project page (photos, progress notes, steps, requests).

const STATUS_LABEL: Record<string, string> = {
  pending: 'Getting ready', scheduled: 'Scheduled', in_progress: 'In progress',
  on_hold: 'Paused for now', completed: 'Complete', invoiced: 'Complete',
  cancelled: 'Cancelled', draft: 'Being planned',
};
const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700', scheduled: 'bg-sky-100 text-sky-700',
  in_progress: 'bg-blue-100 text-blue-700', on_hold: 'bg-gray-100 text-gray-600',
  completed: 'bg-green-100 text-green-700', invoiced: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500', draft: 'bg-gray-100 text-gray-600',
};

// DATE columns arrive as "YYYY-MM-DD" — parse at noon so the day never slips.
const fmtShort = (d: string) =>
  new Date(String(d).slice(0, 10) + 'T12:00:00')
    .toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });

export const ClientJobsPage = () => {
  const [jobs, setJobs] = useState<PortalJob[]>([]);
  const [progress, setProgress] = useState<Map<number, ClientJobProgress>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [jobList, overview] = await Promise.all([
        getPortalJobs(),
        getClientOverview().catch(() => null),
      ]);
      setJobs(jobList);
      if (overview) {
        setProgress(new Map(overview.jobs.map(j => [Number(j.id), j])));
      }
      setError('');
    } catch {
      setError('We couldn\'t load your projects. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-gray-100" />)}
    </div>
  );
  if (error) return (
    <div className="px-4 py-6 text-base text-red-700 flex items-center gap-2 bg-red-50 rounded-xl border border-red-200">
      <span className="material-symbols-outlined">error</span>{error}
    </div>
  );

  // Live work first, finished work after — same order the office uses.
  const ORDER = ['in_progress', 'scheduled', 'pending', 'draft', 'on_hold', 'completed', 'invoiced', 'cancelled'];
  const sorted = [...jobs].sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status));

  return (
    <PullToRefresh onRefresh={load}>
      <div className="space-y-5">
        <LargeTitleHeader
          title="Your projects"
          subtitle="Everything we're building for you, past and present"
        />

        {sorted.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
            <span className="material-symbols-outlined text-gray-300 text-5xl">home_work</span>
            <p className="text-base font-semibold text-gray-600 mt-3">No projects yet</p>
            <p className="text-sm text-gray-400 mt-1">When we start a project for you, it will show up here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sorted.map(job => {
              const p = progress.get(Number(job.id));
              const pct = p && Number(p.steps_total) > 0
                ? Math.round((Number(p.steps_done) / Number(p.steps_total)) * 100)
                : null;
              const j = job as PortalJob & { start_date?: string | null; end_date?: string | null };
              return (
                <Link key={job.id} to={`/portal/client/jobs/${job.id}`}
                  className="block bg-white rounded-2xl border border-gray-200 p-5 active:bg-[#FAF6F0]">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-lg font-black text-[#2C1810] leading-tight">{job.title}</p>
                    <span className={`flex-shrink-0 text-xs px-2.5 py-1 rounded-full font-bold ${STATUS_COLOR[job.status] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_LABEL[job.status] || job.status}
                    </span>
                  </div>
                  {(j.start_date || j.end_date) && (
                    <p className="text-sm text-gray-500 mt-1.5">
                      {j.start_date && <>Runs from {fmtShort(j.start_date)}</>}
                      {j.start_date && j.end_date && <> to {fmtShort(j.end_date)}</>}
                      {!j.start_date && j.end_date && <>Planned finish {fmtShort(j.end_date)}</>}
                    </p>
                  )}
                  {job.site_address && (
                    <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                      <span className="material-symbols-outlined text-base text-gray-400">location_on</span>
                      <span className="truncate">{job.site_address}</span>
                    </p>
                  )}
                  {pct !== null && p && (
                    <>
                      <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden mt-3">
                        <div className="h-full rounded-full bg-[#C77D3F] transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
                      </div>
                      <p className="text-xs text-gray-500 mt-1.5">
                        {pct === 0 ? 'Getting started' : pct === 100 ? 'All steps finished' : `${pct}% done`}
                        {' — '}{Number(p.steps_done)} of {Number(p.steps_total)} steps finished
                        {Number(p.steps_active) > 0 ? `, ${Number(p.steps_active)} under way` : ''}.
                      </p>
                    </>
                  )}
                  <p className="text-sm text-[#C77D3F] font-bold mt-2.5 flex items-center gap-1">
                    See photos, progress &amp; details
                    <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </PullToRefresh>
  );
};
